import posthog from 'posthog-js';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import { positionChangeQueue } from '~/editor/lib/PositionChangeQueue';
import { globalPendingRequestTracker } from '~/editor/lib/PendingRequestTracker';
import type {
  GetTrainingScriptResponse,
  TranslationUpdatePatch,
  UpdateTranslationsResponse,
  ApiErrorResult
} from '~/editor/translation/types';
import {
  appendImageGenerationContext,
  ImageGenerationPollingError,
  resolveImageGenerationStatusUrl,
  waitForGeneratedImage,
  type ImageGenerationContext,
  type ImageGenerationEnvelope
} from './imageGenerationPolling';

const createPositionRequestId = (
  entityType: 'sequence' | 'subitem' | 'chapter',
  entityId: number | string
): string => {
  return `${entityType}-${entityId}-position`;
};

const cancelEntityPositionRequests = (requestId: string): void => {
  positionChangeQueue.cancelMatchingRequests(req => req.id === requestId);
};

const MB = 1024 * 1024;
const GB = MB * 1024;

const FILE_SIZE_LIMITS = {
  PICTURE: 20 * MB,
  AUDIO: 20 * MB,
  VIDEO: 1 * GB,
  DOCUMENT: 50 * MB
} as const;

interface PublishTrainingParams {
  should_auto_update_translations: boolean;
}

export class ApiService {
  private baseUrl = `${process.env.REACT_APP_BACKEND_API_BASE_URL}/api`;
  private token: string;
  private companyId;

  constructor(token: string, companyId: number | string) {
    this.token = token;
    this.companyId = companyId;
  }

  private handleUnauthorizedError(): ApiErrorResult {
    useToastStore.getState().add({
      severity: ToastSeverity.Error,
      message: 'Your session has expired. Please log in again.'
    });
    return {
      error: true as const,
      message: 'Unauthorized: Please log in again',
      status: 401
    };
  }

  private async trackedFetch(
    url: string,
    options: RequestInit,
    skipTracking = false
  ): Promise<Response> {
    const method = options.method || 'GET';

    if (!skipTracking) {
      globalPendingRequestTracker.incrementIfMutating(method);
    }
    // this header allows Posthog to automatically link the frontend replays with the rails backend events
    try {
      const sessionId = posthog.get_session_id?.();
      return await fetch(url, {
        ...options,
        headers: {
          ...(options.headers || {}),
          ...(sessionId && { 'X-Posthog-Session-Id': sessionId })
        }
      });
    } finally {
      if (!skipTracking) {
        globalPendingRequestTracker.decrementIfMutating(method);
      }
    }
  }

  async getTrainingScript(
    trainingId: number | string,
    params: {
      sourceLocale: string;
      targetLocale?: string;
      sequenceId?: number | 'all' | null;
      chapterId?: number | string | null;
    }
  ): Promise<GetTrainingScriptResponse> {
    const search = new URLSearchParams();
    const sourceLocale = params.sourceLocale || 'en';

    search.set('source_locale', sourceLocale);
    if (params.targetLocale) {
      search.set('target_locale', params.targetLocale);
    }
    if (params.sequenceId && params.sequenceId !== 'all') {
      search.set('sequence_id', String(params.sequenceId));
    }
    if (params.chapterId) {
      search.set('chapter_id', String(params.chapterId));
    }

    const url = `${this.baseUrl}/companies/${
      this.companyId
    }/course_editor/trainings/${trainingId}/script?${search.toString()}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();
        const message =
          errorData?.errors?.join(' ') ||
          `Fetching training script failed: ${response.statusText}`;
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message
        });
        return { error: true as const, message };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch training script.'
      });
      return { error: true as const, message: 'Network error' };
    }
  }

  async getCourseInfo(courseId?: number | string) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/trainings/${courseId}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Fetching course info: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch course info.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async getCompanyWithFeatureFlags() {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/companies/${this.companyId}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Fetching company info: ${response.statusText}`
          });
        }
        return { error: true, message: 'Network error' };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch company info.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async getTemplates() {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/templates`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Fetching templates: ${response.statusText}`
          });
        }
      }

      const data = await response.json();
      const groupedTemplates = data.templates.reduce(
        (acc: Record<string, unknown[]>, template: { kind: string }) => {
          const kind = template.kind;
          if (!acc[kind]) {
            acc[kind] = [];
          }
          acc[kind].push(template);
          return acc;
        },
        {}
      );

      return groupedTemplates;
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch templates.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async getSequenceInfo(sequenceId: number) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/sequences/${sequenceId}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Fetching sequence info: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch sequence info.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async createSequenceByTemplate(
    trainingId: number,
    templateId: number | string,
    chapterId: number,
    position: number,
    sequenceName: string
  ) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/chapter_sequences`;

    const body = {
      sequence: {
        title_en: sequenceName
      },
      training_id: trainingId,
      template_id: templateId,
      chapter_id: chapterId,
      position: position || 1
    };

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Creating sequence: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to create sequence.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async deleteSequence(chapterSequenceId: number) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/chapter_sequences/${chapterSequenceId}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Deleting sequence: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to delete sequence.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async copySequence(chapterSequenceId: number, trainingId: number) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/chapter_sequences/${chapterSequenceId}/copy?training_id=${trainingId}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Copying sequence: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to copy sequence.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async moveSequence(
    chapterId: number,
    chapterSequenceId: number,
    newPosition: number
  ) {
    const requestId = createPositionRequestId('sequence', chapterSequenceId);
    cancelEntityPositionRequests(requestId);

    return positionChangeQueue.addRequest(
      async () => {
        const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/chapter_sequences/${chapterSequenceId}/move`;
        const body = {
          position: newPosition,
          chapter_id: chapterId
        };

        try {
          const response = await this.trackedFetch(
            url,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${this.token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(body)
            },
            true
          );

          if (!response.ok) {
            if (response.status === 401) {
              return this.handleUnauthorizedError();
            }

            const errorData = await response.json();

            if (errorData.errors && errorData.errors.length > 0) {
              useToastStore.getState().add({
                severity: ToastSeverity.Error,
                message: errorData.errors.join(' ')
              });
            } else {
              useToastStore.getState().add({
                severity: ToastSeverity.Error,
                message: `Moving sequence: ${response.statusText}`
              });
            }

            return errorData;
          }

          return response.json();
        } catch (error) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: 'Network error: Unable to move sequence.'
          });
          return { error: true, message: 'Network error' };
        }
      },
      { id: requestId, timeoutMs: 10000 }
    );
  }

  async updateSubItem(
    subItemId: number | string,
    fieldType: string,
    value: unknown
  ) {
    const isPositionUpdate = fieldType === 'position';
    const requestId = isPositionUpdate
      ? createPositionRequestId('subitem', subItemId)
      : undefined;

    if (isPositionUpdate && requestId) {
      cancelEntityPositionRequests(requestId);
    }

    const updateFn = async () => {
      const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/subitems/${subItemId}`;

      const body = {
        subitem: {
          [fieldType]: value
        }
      };

      try {
        const response = await this.trackedFetch(
          url,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${this.token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          },
          isPositionUpdate
        );

        if (!response.ok) {
          if (response.status === 401) {
            return this.handleUnauthorizedError();
          }
          const errorData = await response.json().catch(() => ({}));
          const fieldErrors = (errorData && errorData.errors) || {};

          if (fieldErrors.title && fieldErrors.title.length > 0) {
            useToastStore.getState().add({
              severity: ToastSeverity.Error,
              message: fieldErrors.title.join(' ')
            });
          }

          if (fieldErrors.subtitle && fieldErrors.subtitle.length > 0) {
            useToastStore.getState().add({
              severity: ToastSeverity.Error,
              message: fieldErrors.subtitle.join(' ')
            });
          }

          if (fieldErrors.body && fieldErrors.body.length > 0) {
            useToastStore.getState().add({
              severity: ToastSeverity.Error,
              message: fieldErrors.body.join(' ')
            });
          }

          if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            useToastStore.getState().add({
              severity: ToastSeverity.Error,
              message: fieldErrors.join('\n ')
            });
          }

          // Don't read body twice — surface a structured failure so awaiters
          // can detect it (was previously masked by `return response.json()`).
          return {
            error: true,
            status: response.status,
            errors: errorData.errors,
            message:
              fieldErrors.title?.[0] ||
              fieldErrors.subtitle?.[0] ||
              fieldErrors.body?.[0] ||
              `Updating subitem: ${response.statusText}`
          };
        }

        return response.json();
      } catch (error) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'Network error: Unable to update subitem.'
        });
        return { error: true, message: 'Network error' };
      }
    };

    if (isPositionUpdate && requestId) {
      return positionChangeQueue.addRequest(updateFn, {
        id: requestId,
        timeoutMs: 10000
      });
    }

    return updateFn();
  }

  async updateSubItemBatch(
    subItemId: number | string,
    fields: Record<string, unknown>
  ) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/subitems/${subItemId}`;

    const body = {
      subitem: fields
    };

    try {
      const response = await this.trackedFetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }
        const errorData = await response.json();

        if (errorData.errors?.title && errorData.errors.title.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.title.join(' ')
          });
        } else if (
          errorData.errors?.subtitle &&
          errorData.errors.subtitle.length > 0
        ) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.subtitle.join(' ')
          });
        } else if (errorData.errors?.body && errorData.errors.body.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.body.join(' ')
          });
        } else if (
          Array.isArray(errorData.errors) &&
          errorData.errors.length > 0
        ) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join('\n ')
          });
        } else if (errorData.errors && typeof errorData.errors === 'object') {
          const messages: string[] = [];
          Object.values(errorData.errors).forEach((errors: unknown) => {
            if (Array.isArray(errors) && errors.length > 0) {
              messages.push(...errors);
            }
          });
          if (messages.length > 0) {
            useToastStore.getState().add({
              severity: ToastSeverity.Error,
              message: messages.join('\n ')
            });
          }
        }

        return { error: true };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to update subitem.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async reorderSubItem(subItemId: number | string, position: number) {
    const requestId = createPositionRequestId('subitem', subItemId);
    cancelEntityPositionRequests(requestId);

    return positionChangeQueue.addRequest(
      async () => {
        const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/subitems/${subItemId}/reorder`;

        const body = {
          position: position
        };

        try {
          const response = await this.trackedFetch(
            url,
            {
              method: 'PATCH',
              headers: {
                Authorization: `Bearer ${this.token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(body)
            },
            true
          );

          if (!response.ok) {
            if (response.status === 401) {
              return this.handleUnauthorizedError();
            }

            const errorData = await response.json();

            if (errorData.errors && errorData.errors.length > 0) {
              useToastStore.getState().add({
                severity: ToastSeverity.Error,
                message: errorData.errors.join(' ')
              });
            } else {
              useToastStore.getState().add({
                severity: ToastSeverity.Error,
                message: `Reordering subitem: ${response.statusText}`
              });
            }
          }

          return response.json();
        } catch (error) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: 'Network error: Unable to update subitem.'
          });
          return { error: true, message: 'Network error' };
        }
      },
      { id: requestId, timeoutMs: 10000 }
    );
  }

  async addSubItem(
    sequenceId: number | string,
    fields: Record<string, unknown>
  ) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/sequences/${sequenceId}/subitems`;

    const body = {
      subitem: fields
    };

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json().catch(() => ({}));

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Adding subitem: ${response.statusText}`
          });
        }

        // Don't read body twice; surface a structured failure so callers /
        // batch awaiters can detect the failure (was previously masked because
        // `return response.json()` below threw and got swallowed by the catch).
        return {
          error: true,
          status: response.status,
          errors: errorData.errors,
          message:
            (errorData.errors && errorData.errors.join(' ')) ||
            `Adding subitem: ${response.statusText}`
        };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to add subitem.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async removeSubItem(subItemId: number | string) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/subitems/${subItemId}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json().catch(() => ({}));

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Removing subitem: ${response.statusText}`
          });
        }

        // Surface a structured failure (see addSubItem note for context).
        return {
          error: true,
          status: response.status,
          errors: errorData.errors,
          message:
            (errorData.errors && errorData.errors.join(' ')) ||
            `Removing subitem: ${response.statusText}`
        };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to remove subitem.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async getMedia(
    type: 'audios' | 'videos' | 'pictures' | 'documents',
    page?: number,
    source?: string
  ) {
    const _type =
      type == 'documents'
        ? 'document_type'
        : type == 'pictures'
          ? 'picture_type'
          : type == 'videos'
            ? 'video_type'
            : 'audio_type';

    const url = `${this.baseUrl}/companies/${
      this.companyId
    }/course_editor/${type}?page=${page || 1}&${_type}=${source || ''}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Fetching media: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch media.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async rewriteSubItem(fields: Record<string, unknown>) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/subitems/rewrite`;

    const body = fields;

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }
        const errorData = await response.json();
        const errors = errorData.errors;
        let errorMessage = 'Unable to rewrite text. Please try again.';

        if (Array.isArray(errors) && errors.length > 0) {
          errorMessage = errors.join(' ');
        } else if (errors && typeof errors === 'object') {
          const fieldErrors = Object.entries(errors).flatMap(
            ([field, messages]) => {
              const details = Array.isArray(messages)
                ? messages.join(', ')
                : String(messages);
              return details ? `${field} ${details}` : [];
            }
          );
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join(' ');
          }
        }

        return { error: true, message: errorMessage };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to rewrite subitem.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async getUnsplashImages(query: string, page: number) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/pictures/unsplash?query=${query}&page=${page}&per_page=9`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Fetching unsplash images: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch unsplash images.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async uploadMedia(
    type: 'audios' | 'videos' | 'pictures' | 'documents',
    file: File,
    signal?: AbortSignal
  ) {
    if (type == 'pictures' && file.size > FILE_SIZE_LIMITS.PICTURE) {
      return { error: true, message: 'File size exceeds 20 MB limit.' };
    }

    if (type == 'audios' && file.size > FILE_SIZE_LIMITS.AUDIO) {
      return { error: true, message: 'File size exceeds 20 MB limit.' };
    }

    if (type == 'videos' && file.size > FILE_SIZE_LIMITS.VIDEO) {
      return { error: true, message: 'File size exceeds 1 GB limit.' };
    }

    if (type == 'documents' && file.size > FILE_SIZE_LIMITS.DOCUMENT) {
      return { error: true, message: 'File size exceeds 50 MB limit.' };
    }

    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/${type}`;

    const _type =
      type == 'pictures'
        ? 'picture'
        : type == 'videos'
          ? 'video'
          : type == 'documents'
            ? 'document'
            : 'audio';

    const formData = new FormData();
    const internalName = file.name
      .replace(/[\r\n]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    formData.append(`${_type}[internal_name]`, internalName);
    if (type === 'documents') {
      formData.append(`${_type}[name]`, internalName);
    }

    formData.append(`${_type}[company_id]`, this.companyId.toString());
    formData.append(`${_type}[${_type}]`, file, '[PROXY]');

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`
        },
        body: formData,
        signal
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }
        const errorData = await response.json();
        let errorMessage = '';

        if (errorData.errors.picture) {
          errorMessage = errorData.errors.picture.join(' ');
        } else if (errorData.errors.video) {
          errorMessage = errorData.errors.video.join(' ');
        } else if (errorData.errors.audio) {
          errorMessage = errorData.errors.audio.join(' ');
        } else {
          if (errorData.errors && errorData.errors.length > 0) {
            errorMessage = errorData.errors.join(' ');
          } else {
            errorMessage = `Uploading media: ${response.statusText}`;
          }
        }

        return { error: true, message: errorMessage };
      }

      return response.json();
    } catch (error: unknown) {
      if ((error as { name?: string }).name === 'AbortError') {
        useToastStore.getState().add({
          severity: ToastSeverity.Warning,
          message: 'Upload cancelled'
        });

        return { error: true, message: 'Upload cancelled' };
      }

      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to upload media.'
      });

      return { error: true, message: 'Network error' };
    }
  }

  async downloadUnsplashImage(id: string, name: string) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/pictures`;

    const _type = 'picture';

    const formData = new FormData();
    formData.append(`${_type}[internal_name]`, name);
    formData.append(`${_type}[company_id]`, this.companyId.toString());
    formData.append(`${_type}[source]`, 'unsplash');
    formData.append(`${_type}[unsplash_id]`, id);

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`
        },
        body: formData
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Downloading unsplash image: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to download unsplash image.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async getVoiceForSequence(sequenceId: number) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/audios?sequence_id=${sequenceId}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Fetching voice for sequence: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch voice for sequence.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async setPreferredVoice(voiceId: string) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/preferred_voice`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ voice_id: voiceId })
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Setting preferred voice: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to set preferred voice.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async getPreferredVoice() {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/preferred_voice`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Fetching preferred voice: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch preferred voice.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async getVoices() {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/voices`;
    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Fetching voices: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch voices.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async searchUnsplash(query: string, page: number) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/pictures/unsplash`;

    const formData = new FormData();
    formData.append(`page`, page.toString() || '1');
    formData.append(`per_page`, '6');
    formData.append(`query`, query);

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Searching unsplash: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to search unsplash.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  private cleanText(text: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;

    // textContent already strips tags (incl. <style>); no regex post-pass needed
    return (tempDiv.textContent || tempDiv.innerText || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  async generateNarration(text: string, voiceId: string, lng: string) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/audios/artificial`;
    const body = {
      text: this.cleanText(text),
      voice_id: voiceId,
      language_code: lng
    };

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }
        const errorData = await response.json();
        let errorMessage = 'Error generating narration';

        if (errorData.error) {
          errorMessage = errorData.error.join(' ');
        }

        return { error: true, message: errorMessage };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to generate narration.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async setNarration(sequenceId: number, audioId: number) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/sequences/${sequenceId}`;

    const body = {
      sequence: {
        audio_ids: [audioId]
      }
    };

    try {
      const response = await this.trackedFetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Setting narration: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to set narration.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async getPreviewUrl(
    trainingId: number,
    sequenceId: number,
    options?: { previewTranslationLocale?: string }
  ) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/trainings/${trainingId}/preview`;

    const body = {
      sequence_id: sequenceId,
      target_locale: options?.previewTranslationLocale
    };

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Getting preview url: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to get preview url.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async deleteChapter(chapterId: number) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/chapters/${chapterId}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Deleting chapter: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to delete chapter.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async updateChapter(chapterId: number, data: Record<string, unknown>) {
    const isPositionUpdate = 'position' in data;
    const requestId = isPositionUpdate
      ? createPositionRequestId('chapter', chapterId)
      : undefined;

    if (isPositionUpdate && requestId) {
      cancelEntityPositionRequests(requestId);
    }

    const updateFn = async () => {
      const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/chapters/${chapterId}`;
      const body = {
        chapter: data
      };

      try {
        const response = await this.trackedFetch(
          url,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${this.token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          },
          isPositionUpdate
        );

        if (!response.ok) {
          if (response.status === 401) {
            return this.handleUnauthorizedError();
          }

          const errorData = await response.json();

          if (errorData.errors && errorData.errors.length > 0) {
            useToastStore.getState().add({
              severity: ToastSeverity.Error,
              message: errorData.errors.join(' ')
            });
          } else {
            useToastStore.getState().add({
              severity: ToastSeverity.Error,
              message: `Updating chapter: ${response.statusText}`
            });
          }
        }

        return response.json();
      } catch (error) {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: 'Network error: Unable to update chapter.'
        });
        return { error: true, message: 'Network error' };
      }
    };

    if (isPositionUpdate && requestId) {
      return positionChangeQueue.addRequest(updateFn, {
        id: requestId,
        timeoutMs: 10000
      });
    }

    return updateFn();
  }

  async updateSequence(sequenceId: number, data: Record<string, unknown>) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/sequences/${sequenceId}`;
    const body = {
      sequence: data
    };

    try {
      const response = await this.trackedFetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Updating sequence: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to update sequence.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async createNewChapter(data: Record<string, unknown>) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/chapters`;
    const body = {
      chapter: data
    };

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Creating new chapter: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to create new chapter.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async publishTraining(trainingId: number, params?: PublishTrainingParams) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/trainings/${trainingId}/publish`;

    try {
      const request = {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      };

      const response = await this.trackedFetch(url, request);

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Publishing training: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to publish training.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async deleteMedia(
    pictureId: number,
    type: 'pictures' | 'videos' | 'audios' | 'documents'
  ) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/${type}/${pictureId}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }
        const errorData = await response.json();
        return {
          error: true,
          message:
            errorData.errors.base && errorData.errors.base.length > 0
              ? errorData.errors.base.join(' ')
              : errorData.errors.join(' ')
        };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to delete media.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async getPexels(query: string, page: number) {
    const url = `${this.baseUrl}/companies/${
      this.companyId
    }/course_editor/videos/pexels?page=${page}&per_page=12&query=${encodeURIComponent(
      query
    )}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Fetching pexels videos: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch pexels videos.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async downloadPexelsVideo(videoId: string, name: string) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/videos`;

    const formData = new FormData();
    formData.append(`video[internal_name]`, name);
    formData.append(`video[pexels_id]`, videoId);

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`
        },
        body: formData
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Downloading pexels video: ${response.statusText}`
          });
        }
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to download pexels video.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async generateImageWithAI(
    prompt: string,
    style: string,
    context?: ImageGenerationContext
  ) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/pictures/generate`;

    const formData = new FormData();
    formData.append('prompt', prompt);
    formData.append('style', style);
    if (context) {
      appendImageGenerationContext(formData, context);
    }

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`
        },
        body: formData
      });

      if (response.status === 401) {
        return this.handleUnauthorizedError();
      }

      const payload = (await response.json()) as ImageGenerationEnvelope & {
        errors?: string[];
      };
      if (!response.ok) {
        if (payload.errors && payload.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: payload.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Generating image with AI: ${response.statusText}`
          });
        }
        return payload;
      }

      return await waitForGeneratedImage(payload, {
        fetchStatus: statusUrl => this.fetchImageGenerationStatus(statusUrl)
      });
    } catch (error) {
      if (
        error instanceof ImageGenerationPollingError &&
        error.code === 'unauthorized'
      ) {
        return this.handleUnauthorizedError();
      }

      const message =
        error instanceof ImageGenerationPollingError
          ? 'Image generation could not be completed. Please try again.'
          : 'Network error: Unable to generate image with AI.';
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message
      });
      return { error: true, message };
    }
  }

  private async fetchImageGenerationStatus(
    statusUrl: string
  ): Promise<ImageGenerationEnvelope> {
    const url = resolveImageGenerationStatusUrl(
      statusUrl,
      process.env.REACT_APP_BACKEND_API_BASE_URL,
      this.companyId
    );

    const response = await this.trackedFetch(
      url,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${this.token}` }
      },
      true
    );
    if (response.status === 401) {
      throw new ImageGenerationPollingError('unauthorized');
    }
    if (!response.ok) {
      throw new ImageGenerationPollingError('status_request_failed');
    }
    return response.json();
  }

  async startAIModification(
    sequenceId: number,
    modificationInstructions: string
  ) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/sequences/${sequenceId}/modify_with_ai`;
    const body = {
      modification_instructions: modificationInstructions
    };

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();
        let errorMessage = 'Error starting AI modification';

        if (errorData.errors) {
          for (const [field, messages] of Object.entries(errorData.errors) as [
            string,
            string[]
          ][]) {
            errorMessage += ` ${field} ${messages.join(', ')}`;
          }
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }

        return { error: true, message: errorMessage };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to start AI modification.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async pollAIModificationStatus(sequenceId: number, aiResponseId: number) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/sequences/${sequenceId}/ai_modification_status?ai_response_id=${aiResponseId}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        if (response.status === 404) {
          return { error: true, message: 'AI response not found' };
        }

        const errorData = await response.json();
        let errorMessage = 'Error checking AI modification status';

        if (errorData.errors) {
          for (const [field, messages] of Object.entries(errorData.errors) as [
            string,
            string[]
          ][]) {
            errorMessage += ` ${field} ${messages.join(', ')}`;
          }
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }

        return { error: true, message: errorMessage };
      }

      return response.json();
    } catch (error) {
      console.error('Network error polling AI status:', error);
      return { error: true, message: 'Network error checking status' };
    }
  }

  async getChatBotTemplates() {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/conversation_bots?type=templates`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Fetching chatbot templates: ${response.statusText}`
          });
        }
        return { error: true, message: 'Failed to fetch chatbot templates' };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch chatbot templates.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async getChatBot(chatBotId: string) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/conversation_bots/${chatBotId}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Fetching chatbot: ${response.statusText}`
          });
        }
        return { error: true, message: 'Failed to fetch chatbot' };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to fetch chatbot.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async createChatBotFromTemplate(
    templateId: string,
    vars: {
      subject: string;
      num_questions: number;
      notes: string;
    }
  ) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/conversation_bots/create_from_template`;

    const body = {
      template_id: parseInt(templateId, 10),
      vars
    };

    try {
      const response = await this.trackedFetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Creating chatbot: ${response.statusText}`
          });
        }
        return { error: true, message: 'Failed to create chatbot' };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to create chatbot.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async updateChatBot(
    chatBotId: string,
    payload: {
      conversation_bot: {
        settings: {
          template: {
            vars: {
              subject: string;
              num_questions: number;
              notes: string;
              level: string;
            };
          };
        };
      };
    }
  ) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/conversation_bots/${chatBotId}`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        } else {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: `Updating chatbot: ${response.statusText}`
          });
        }
        return { error: true, message: 'Failed to update chatbot' };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to update chatbot.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async updateTrainingTitle(trainingId: number, title: string) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/trainings/${trainingId}`;
    const body = {
      training: { title: title }
    };

    try {
      const response = await this.trackedFetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();

        if (errorData.errors && errorData.errors.length > 0) {
          useToastStore.getState().add({
            severity: ToastSeverity.Error,
            message: errorData.errors.join(' ')
          });
        }

        return errorData;
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to update training title.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async updateTranslations(
    trainingId: number | string,
    patch: TranslationUpdatePatch
  ): Promise<UpdateTranslationsResponse> {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/trainings/${trainingId}/translations`;

    try {
      const response = await this.trackedFetch(url, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(patch)
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }
        const errorData = await response.json();
        const message =
          errorData?.errors?.join(' ') ||
          `Updating translations failed: ${response.statusText}`;
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message
        });
        return { error: true as const, message };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to update translations.'
      });
      return { error: true as const, message: 'Network error' };
    }
  }

  async undoSequence(sequenceId: number) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/sequences/${sequenceId}/undo`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: errorData.errors?.[0] || 'Failed to undo changes'
        });
        return { error: true, message: errorData.errors?.[0] };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to undo changes.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async redoSequence(sequenceId: number) {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/sequences/${sequenceId}/redo`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const errorData = await response.json();
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: errorData.errors?.[0] || 'Failed to redo changes'
        });
        return { error: true, message: errorData.errors?.[0] };
      }

      return response.json();
    } catch (error) {
      useToastStore.getState().add({
        severity: ToastSeverity.Error,
        message: 'Network error: Unable to redo changes.'
      });
      return { error: true, message: 'Network error' };
    }
  }

  async fetchGlobalPlaceholders() {
    const url = `${this.baseUrl}/companies/${this.companyId}/course_editor/global_placeholders`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          return this.handleUnauthorizedError();
        }

        const message = `Failed to fetch global placeholders: ${response.statusText}`;
        return {
          error: true as const,
          message,
          status: response.status
        };
      }

      try {
        return await response.json();
      } catch (jsonError) {
        const message =
          jsonError instanceof Error ? jsonError.message : 'Invalid JSON';
        return {
          error: true as const,
          message: `Failed to parse response: ${message}`,
          status: response.status
        };
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Network error';
      return {
        error: true as const,
        message: `Network error: ${message}`,
        status: undefined
      };
    }
  }
}
